/**
 * Google OAuth Callback Page
 *
 * Handles the OAuth redirect from Google.
 * Extracts the authorization code and sends it to the parent window.
 */

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './GoogleOAuthCallback.css';

type CallbackStatus = 'processing' | 'success' | 'error';

export function GoogleOAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<CallbackStatus>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    // Handle error from Google
    if (error) {
      setStatus('error');
      setErrorMessage(errorDescription || error);

      // Send error to parent if in popup
      if (window.opener) {
        window.opener.postMessage(
          {
            type: 'google-oauth-callback',
            error: errorDescription || error,
          },
          window.location.origin
        );
        setTimeout(() => window.close(), 2000);
      }
      return;
    }

    // Validate required params
    if (!code || !state) {
      setStatus('error');
      setErrorMessage('Parametri di autorizzazione mancanti');
      return;
    }

    // Send code to parent window if in popup
    if (window.opener) {
      window.opener.postMessage(
        {
          type: 'google-oauth-callback',
          code,
          state,
        },
        window.location.origin
      );
      setStatus('success');

      // Close popup after short delay
      setTimeout(() => window.close(), 1500);
    } else {
      // Not in popup - redirect to settings with params
      // The settings page will handle the code exchange
      navigate(`/settings/integrations?google_code=${code}&google_state=${state}`, {
        replace: true,
      });
    }
  }, [searchParams, navigate]);

  return (
    <div className="oauth-callback">
      <div className="oauth-callback-content">
        {status === 'processing' && (
          <>
            <div className="oauth-callback-spinner" />
            <h2>Connecting...</h2>
            <p>We are completing the connection with Google.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="oauth-callback-icon oauth-callback-icon--success">
              <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2>Connection successful!</h2>
            <p>This window will close automatically.</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="oauth-callback-icon oauth-callback-icon--error">
              <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M15 9l-6 6M9 9l6 6" strokeLinecap="round"/>
              </svg>
            </div>
            <h2>Connection failed</h2>
            <p>{errorMessage || 'An error occurred during the connection.'}</p>
            {!window.opener && (
              <button
                className="oauth-callback-btn"
                onClick={() => navigate('/settings/integrations')}
              >
                Back to settings
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default GoogleOAuthCallback;
